/**
 * Service for checking if a file is supported by Autodesk Viewer
 * Uses the Autodesk Model Derivative API formats endpoint
 */

// Cache for supported formats to avoid repeated API calls
let supportedExtensionsCache = null;
let formatsCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get supported file extensions from Autodesk Model Derivative API
 * @param {string} accessToken - The access token for authentication
 * @returns {Promise<string[]>} - Array of supported file extensions (lowercase)
 */
const getSupportedExtensions = async (accessToken) => {
    // Check if we have a valid cached result
    if (supportedExtensionsCache && formatsCacheTimestamp && 
        (Date.now() - formatsCacheTimestamp) < CACHE_DURATION) {
        // console.log('Using cached supported extensions');
        return supportedExtensionsCache;
    }

    try {
        const requestHeaders = new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
        });

        const apiUrl = `https://developer.api.autodesk.com/modelderivative/v2/designdata/formats`;

        const response = await fetch(new Request(apiUrl, {
            method: 'GET',
            headers: requestHeaders
        }));

        if (!response.ok) {
            throw new Error(`Formats API failed with status: ${response.status}`);
        }

        const formatsData = await response.json();
        // console.log('Supported formats response:', formatsData);

        // Extract file extensions from thumbnail formats (these are viewable in the viewer)
        const supportedExtensions = [];
        if (formatsData.formats && formatsData.formats.thumbnail) {
            formatsData.formats.thumbnail.forEach(ext => {
                // Clean up the extension (remove regex patterns and convert to lowercase)
                let cleanExt = ext.toLowerCase();
                
                // Remove regex patterns like "asm\\.\\d+$" and just keep "asm"
                if (cleanExt.includes('\\')) {
                    cleanExt = cleanExt.split('\\')[0];
                }
                
                // Add to supported extensions if not already there
                if (!supportedExtensions.includes(cleanExt)) {
                    supportedExtensions.push(cleanExt);
                }
            });
        }

        // console.log('Extracted supported extensions:', supportedExtensions);

        // Cache the result
        supportedExtensionsCache = supportedExtensions;
        formatsCacheTimestamp = Date.now();

        return supportedExtensions;
    } catch (error) {
        console.error('Error fetching supported formats:', error);
        throw error;
    }
};

/**
 * Extract file extension from file name
 * @param {string} fileName - The file name
 * @returns {string|null} - The file extension (without dot, lowercase) or null
 */
const extractFileExtension = (fileName) => {
    if (!fileName) return null;
    
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return null;
    
    const extension = fileName.substring(lastDotIndex + 1).toLowerCase();
    // console.log(`Extracted extension "${extension}" from "${fileName}"`);
    return extension;
};

/**
 * Checks if a file is supported by the Autodesk Viewer based on its name
 * @param {string} fileName - The file name to check
 * @param {string} accessToken - The access token for authentication
 * @returns {Promise<boolean>} - True if the file type is supported by the viewer
 */
export const checkViewerSupport = async (fileName, accessToken) => {
    // console.log('=== checkViewerSupport DEBUG START ===');
    // console.log('File Name:', fileName);
    // console.log('Access Token:', accessToken ? 'Present' : 'Missing');
    
    if (!fileName || !accessToken) {
        // console.warn('Missing file name or access token for viewer support check');
        // console.log('=== checkViewerSupport DEBUG END (early return) ===');
        return false;
    }

    try {
        // Get supported file extensions
        // console.log('Getting supported extensions...');
        const supportedExtensions = await getSupportedExtensions(accessToken);
        
        // Extract file extension from the file name
        const fileExtension = extractFileExtension(fileName);
        
        if (!fileExtension) {
            // console.log('Could not determine file extension, assuming not supported');
            // console.log('=== checkViewerSupport DEBUG END (no extension) ===');
            return false;
        }

        // Check if the file extension is supported
        const isSupported = supportedExtensions.includes(fileExtension);

        // console.log(`File extension "${fileExtension}" supported:`, isSupported);
        // console.log('Supported extensions list:', supportedExtensions);
        // console.log('=== checkViewerSupport DEBUG END (success) ===');
        return isSupported;

    } catch (error) {
        console.error('Error checking viewer support:', error);
        // console.log('=== checkViewerSupport DEBUG END (error) ===');
        // In case of network errors or other issues, assume not supported
        return false;
    }
};

